"use client"

import { useState, useEffect, useMemo } from "react"
import { ArrowLeft, Tag, Search, Star, TrendingUp, Award } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { getProducts, type ProductDisplay } from "@/lib/api/dutchie"

interface Brand {
  name: string;
  slug: string;
  count: number;
  firstLetter: string;
  /** Up to 4 representative in-stock product images for this brand. */
  images: string[];
}

/**
 * Brand-page redesign v4, product thumbnails inside each card.
 *
 * Adds a strip of up to 4 product thumbnails per brand (real product
 * photos pulled from live Dutchie data). Card height grows modestly
 * (~76px → ~120px) so the page doesn't balloon vertically.
 *
 * Falls back gracefully when a brand has no usable images, the card
 * just renders without the thumbnail strip and stays at the original
 * compact height.
 */

const MONOGRAM_GRADIENTS = [
  "from-emerald-500 to-green-700",
  "from-amber-500 to-orange-600",
  "from-blue-500 to-indigo-700",
  "from-purple-500 to-fuchsia-700",
  "from-rose-500 to-red-700",
  "from-teal-500 to-cyan-700",
  "from-lime-500 to-green-600",
  "from-violet-500 to-purple-700",
];

function pickGradient(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return MONOGRAM_GRADIENTS[hash % MONOGRAM_GRADIENTS.length];
}

type Tier = "heavy" | "deep" | "broad" | "core" | "small";
function tierFor(count: number): Tier {
  if (count >= 80) return "heavy";
  if (count >= 40) return "deep";
  if (count >= 20) return "broad";
  if (count >= 5) return "core";
  return "small";
}

/**
 * Same filter rule as the category page, skip placeholders and svg
 * logos so we end up with real product photos.
 */
function isUsableImage(url: string | undefined | null): url is string {
  if (!url) return false;
  if (url.includes("placeholder")) return false;
  if (url.endsWith(".svg")) return false;
  return true;
}

interface BrandTileProps {
  brand: Brand;
  featured?: boolean;
}

/**
 * Standard brand card, name + count + thumbnail strip.
 * Visual weight still scales with stock (stripe width + name size +
 * amber ring on the top tier).
 */
function BrandTile({ brand, featured = false }: BrandTileProps) {
  const tier = tierFor(brand.count);
  const isHeavy = tier === "heavy";
  const isDeep = tier === "deep";
  const hasImages = brand.images.length > 0;

  const stripeWidth = isHeavy ? "w-1.5" : isDeep ? "w-1" : "w-0.5";
  const nameSize = isHeavy
    ? "text-base"
    : isDeep
      ? "text-[15px]"
      : "text-sm";

  return (
    <Link
      href={`/shop/brand/${brand.slug}`}
      className={`group relative flex flex-col p-3 pl-4 rounded-xl bg-white border border-gray-100 hover:border-primary_green/40 hover:shadow-md transition-all overflow-hidden ${
        hasImages ? "min-h-[120px]" : "min-h-[76px]"
      } ${isHeavy ? "ring-1 ring-amber-100" : ""}`}
    >
      <div
        className={`absolute left-0 top-0 bottom-0 ${stripeWidth} bg-gradient-to-b ${pickGradient(brand.name)}`}
        aria-hidden="true"
      />

      {/* Name + count header */}
      <div className="pl-1">
        <h3
          className={`${nameSize} font-bold text-gray-900 group-hover:text-primary_green transition-colors leading-tight line-clamp-1`}
        >
          {brand.name}
        </h3>
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <span className="tabular-nums font-medium text-gray-700">
              {brand.count}
            </span>
            {brand.count === 1 ? "product" : "products"}
            {featured && <span className="text-amber-500" aria-label="featured">★</span>}
          </span>
          {isHeavy && (
            <span className="text-[9px] uppercase tracking-wider font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
              Top
            </span>
          )}
        </div>
      </div>

      {/* Thumbnail strip, only show actual product images, no
          placeholder squares. Fixed-size tiles so a brand with 1
          product shows one thumb left-aligned (not stretched to fill). */}
      {hasImages && (
        <div className="mt-auto pt-2 flex gap-1">
          {brand.images.slice(0, 4).map((src, i) => (
            <div
              key={i}
              className="relative w-9 h-9 rounded-md overflow-hidden bg-gray-50 border border-gray-100 shrink-0"
            >
              <Image
                src={src}
                alt=""
                fill
                sizes="36px"
                className="object-cover"
                unoptimized
              />
            </div>
          ))}
        </div>
      )}
    </Link>
  );
}

/**
 * Hero editorial card, bigger thumbnails (4 across) sit below the
 * name + count stat.
 */
function FeaturedBrandCard({
  brand,
  badge,
  icon: Icon,
}: {
  brand: Brand;
  badge: string;
  icon: typeof Star;
}) {
  const hasImages = brand.images.length > 0;
  return (
    <Link
      href={`/shop/brand/${brand.slug}`}
      className="group relative flex flex-col rounded-2xl bg-white border border-gray-100 hover:border-primary_green/40 hover:shadow-lg transition-all overflow-hidden"
    >
      <div
        className={`h-2 bg-gradient-to-r ${pickGradient(brand.name)}`}
        aria-hidden="true"
      />
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-1.5 mb-2">
          <Icon className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
          <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-amber-600">
            {badge}
          </span>
        </div>
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-xl font-extrabold text-gray-900 group-hover:text-primary_green transition-colors leading-tight line-clamp-2">
            {brand.name}
          </h3>
          <div className="text-right shrink-0">
            <div className="text-2xl font-extrabold tabular-nums text-gray-900 leading-none">
              {brand.count}
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5 uppercase tracking-wider">
              products
            </div>
          </div>
        </div>

        {hasImages && (
          <div className="mt-4 flex gap-1.5">
            {brand.images.slice(0, 4).map((src, i) => (
              <div
                key={i}
                className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-50 border border-gray-100 shrink-0"
              >
                <Image
                  src={src}
                  alt=""
                  fill
                  sizes="64px"
                  className="object-cover group-hover:scale-105 transition-transform duration-300"
                  unoptimized
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function AllBrandsPage() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    async function loadBrands() {
      setLoading(true);
      try {
        const products = await getProducts();
        const uniqueBrands: Record<string, Brand> = {};

        // First pass: collect names + counts + image candidates.
        products.forEach((product: ProductDisplay) => {
          let brandName = "Unknown";

          if (typeof product.brand === "string" && product.brand) {
            brandName = product.brand;
          } else if (product.brand && typeof product.brand === "object") {
            const brandObj = product.brand as { name: string };
            if (brandObj.name) brandName = brandObj.name;
          }

          if (!brandName || brandName === "Unknown") return;

          const slug = brandName.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

          if (!uniqueBrands[slug]) {
            uniqueBrands[slug] = {
              name: brandName,
              slug,
              count: 0,
              firstLetter: brandName.replace(/^[^A-Za-z0-9]+/, "").charAt(0).toUpperCase() || "#",
              images: [],
            };
          }
          uniqueBrands[slug].count++;

          // Collect up to 4 distinct in-stock product images per brand.
          // Prefer in-stock products; fall back to anything with an image.
          if (
            uniqueBrands[slug].images.length < 4 &&
            isUsableImage(product.image) &&
            !uniqueBrands[slug].images.includes(product.image)
          ) {
            uniqueBrands[slug].images.push(product.image);
          }
        });

        const sorted = Object.values(uniqueBrands).sort((a, b) => a.name.localeCompare(b.name));
        setBrands(sorted);
      } catch (err) {
        console.error("Error loading brands:", err);
      } finally {
        setLoading(false);
      }
    }
    loadBrands();
  }, []);

  const filteredBrands = useMemo(() => {
    if (!searchQuery.trim()) return brands;
    const q = searchQuery.toLowerCase();
    return brands.filter((b) => b.name.toLowerCase().includes(q));
  }, [brands, searchQuery]);

  const sortedByCount = useMemo(
    () => [...brands].sort((a, b) => b.count - a.count),
    [brands]
  );
  const featuredBrands = useMemo(
    () => (searchQuery.trim() ? [] : sortedByCount.slice(0, 3)),
    [sortedByCount, searchQuery]
  );
  const morePopular = useMemo(
    () => (searchQuery.trim() ? [] : sortedByCount.slice(3, 8)),
    [sortedByCount, searchQuery]
  );

  const groupedBrands = useMemo(() => {
    const groups: Record<string, Brand[]> = {};
    filteredBrands.forEach((brand) => {
      const key = /^[A-Z]$/.test(brand.firstLetter) ? brand.firstLetter : "#";
      if (!groups[key]) groups[key] = [];
      groups[key].push(brand);
    });
    return groups;
  }, [filteredBrands]);

  const letters = useMemo(() => {
    const keys = Object.keys(groupedBrands);
    return keys.sort((a, b) => {
      if (a === "#") return 1;
      if (b === "#") return -1;
      return a.localeCompare(b);
    });
  }, [groupedBrands]);

  const totalProducts = useMemo(
    () => brands.reduce((sum, b) => sum + b.count, 0),
    [brands]
  );

  const FEATURED_BADGES: Array<{ label: string; icon: typeof Star }> = [
    { label: "Top Stocked", icon: Award },
    { label: "Wide Variety", icon: Star },
    { label: "Popular", icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-20 -mt-20" />
        <div className="absolute bottom-0 left-1/3 w-48 h-48 bg-white/5 rounded-full -mb-24" />
        <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 hidden md:block" />

        <div className="container mx-auto px-4 py-8 md:py-10 relative">
          <Link
            href="/shop"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 text-sm font-medium transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Shop
          </Link>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-2">
                Shop by Brand
              </p>
              <h1 className="text-4xl md:text-6xl font-extrabold leading-none">
                <span className="tabular-nums">{brands.length || "—"}</span>
                <span className="text-2xl md:text-3xl font-bold text-white/85 ml-3">
                  Brand{brands.length !== 1 ? "s" : ""}
                </span>
              </h1>
              <p className="text-white/85 mt-3 max-w-md text-sm md:text-base">
                Curated cannabis brands from across New York, from local
                Buffalo growers to coast-to-coast favorites.
              </p>
            </div>

            {brands.length > 0 && (
              <div className="flex gap-2 md:gap-3">
                <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2.5">
                  <div className="text-2xl font-bold tabular-nums leading-none">
                    {brands.length}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-white/80 mt-1">
                    Brands
                  </div>
                </div>
                <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2.5">
                  <div className="text-2xl font-bold tabular-nums leading-none">
                    {totalProducts}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-white/80 mt-1">
                    Products
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky search + letter index */}
      <div className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-3 space-y-3">
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${brands.length || ""} brands…`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus={false}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-primary_green focus:ring-2 focus:ring-primary_green/20"
            />
          </div>

          {!searchQuery.trim() && letters.length > 0 && (
            <nav
              aria-label="Jump to letter"
              className="flex items-center justify-center flex-wrap gap-1.5"
            >
              {letters.map((letter) => (
                <a
                  key={letter}
                  href={`#letter-${letter}`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-gray-600 hover:text-primary_green hover:bg-primary_green/10 font-semibold transition-colors"
                >
                  <span>{letter}</span>
                  <span className="text-[10px] text-gray-400 font-medium tabular-nums">
                    {groupedBrands[letter].length}
                  </span>
                </a>
              ))}
            </nav>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 pb-12">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="flex flex-col items-center">
              <div className="h-14 w-14 bg-gradient-to-br from-amber-100 to-amber-200 rounded-2xl mb-4 animate-pulse" />
              <div className="h-4 w-36 bg-gray-200 rounded-full animate-pulse" />
            </div>
          </div>
        ) : filteredBrands.length === 0 ? (
          <div className="text-center py-16">
            <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              No brands found matching &quot;{searchQuery}&quot;
            </p>
          </div>
        ) : (
          <div className="space-y-10 pt-6">
            {featuredBrands.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Award className="h-5 w-5 text-amber-500" />
                  <h2 className="text-lg font-bold text-gray-900">
                    Featured Brands
                  </h2>
                  <span className="text-xs text-gray-500 ml-1">
                    Highest-stocked right now
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {featuredBrands.map((brand, i) => {
                    const badge = FEATURED_BADGES[i] || FEATURED_BADGES[0];
                    return (
                      <FeaturedBrandCard
                        key={brand.slug}
                        brand={brand}
                        badge={badge.label}
                        icon={badge.icon}
                      />
                    );
                  })}
                </div>
              </section>
            )}

            {morePopular.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                  <h2 className="text-lg font-bold text-gray-900">
                    More Popular Brands
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {morePopular.map((brand) => (
                    <BrandTile key={brand.slug} brand={brand} featured />
                  ))}
                </div>
              </section>
            )}

            {letters.map((letter) => (
              <section key={letter} id={`letter-${letter}`} className="scroll-mt-32">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary_green to-emerald-700 text-white font-bold flex items-center justify-center">
                    {letter}
                  </span>
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    {groupedBrands[letter].length}{" "}
                    {groupedBrands[letter].length === 1 ? "brand" : "brands"}
                  </h2>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {groupedBrands[letter].map((brand) => (
                    <BrandTile key={brand.slug} brand={brand} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
